export default class GridPacker {
    /**
     * This class is used to pack a grid of 2d boxes with various sizes as
     * tightly as possible, while maintaining the order of the boxes when
     * feasible.
     *
     * @param {number} gridWidth - measured in cells (integer)
     */
    constructor(gridWidth) {
        this.gridWidth = gridWidth;
    }

    /**
     * Packs the given items, assigning each one a non-overlapping position in
     * the grid. Starts at the top-left and moves to the right before moving
     * down.
     *
     * @param {number[]|object[]} items -
     *   An array of objects with `width` and `height` fields measured in cells.
     *   Optionally, a number can be given instead, where the number is both the
     *   width and the height.
     * @returns {object[]} packedItems - items are copied from input array
     * @returns {number}   packedItems[].x
     * @returns {number}   packedItems[].y
     * @returns {number}   packedItems[].width
     * @returns {number}   packedItems[].height
     */
    pack(items) {
        items = Object.assign([], items).map(i => {
            if (typeof i === 'number') {
                return { width: i, height: i };
            } else {
                return Object.assign({}, i);
            }
        });

        const occupiedSpace = new Dynamic2dArray(false);
        const doesItemFit = (item, x, y) => {
            if (x < 0 || y < 0 || x + item.width > this.gridWidth) {
                return false;
            }
            const x1 = x;
            const y1 = y;
            const x2 = x + item.width - 1;
            const y2 = y + item.height - 1;
            for (x = x1; x <= x2; ++x) {
                for (y = y1; y <= y2; ++y) {
                    if (occupiedSpace.get(x, y)) {
                        return false;
                    }
                }
            }
            return true;
        };
        const reserveSpace = (item, x, y) => {
            const x1 = x;
            const y1 = y;
            const x2 = x + item.width - 1;
            const y2 = y + item.height - 1;
            for (x = x1; x <= x2; ++x) {
                for (y = y1; y <= y2; ++y) {
                    occupiedSpace.set(x, y, true);
                }
            }
        };

        let unpackedItems = Object.assign([], items);
        let packedItems = [];
        let xPos = 0;
        let yPos = 0;

        while (unpackedItems.length > 0) {
            const item = unpackedItems.find(item => {
                return doesItemFit(item, xPos, yPos);
            });

            if (item) {
                reserveSpace(item, xPos, yPos);
                item.x = xPos;
                item.y = yPos;
                packedItems[items.indexOf(item)] = item;
                unpackedItems = unpackedItems.filter(i => i !== item);

                xPos += item.width;
            } else {
                xPos += 1;
            }

            if (xPos >= this.gridWidth) {
                xPos = 0;
                yPos += 1;
            }
        }

        return packedItems;
    }

    /**
     * After packing items using pack(), this method will find the holes (if any)
     * within the packed structure. All holes returned are 1x1; no attempt is
     * made to merge adjacent holes.
     *
     * @param {object[]} packedItems - returned from pack()
     * @returns {object[]} holes
     * @returns {number}   holes[].x
     * @returns {number}   holes[].y
     * @returns {number}   holes[].width
     * @returns {number}   holes[].height
     */
    findHoles(packedItems) {
        const width = this.gridWidth;
        const height = (() => {
            let height = 0;
            for (const pItem of packedItems) {
                if (height < pItem.y + pItem.height) {
                    height = pItem.y + pItem.height;
                }
            }
            return height;
        })();

        function doesItemContain(pItem, x, y) {
            return (x >= pItem.x &&
                    x <  pItem.x + pItem.width &&
                    y >= pItem.y &&
                    y <  pItem.y + pItem.height);
        }

        let holes = [];
        for (let x = 0; x < width; ++x) {
            for (let y = 0; y < height; ++y) {
                const empty = !packedItems.find(pItem => {
                    return doesItemContain(pItem, x, y);
                });
                if (empty) {
                    holes.push({
                        x,
                        y,
                        width: 1,
                        height: 1,
                    });
                }
            }
        }
        return holes;
    }
}

class Dynamic2dArray {
    constructor(defaultVal) {
        this.values = {};
        this.defaultVal = defaultVal;
    }

    set(x, y, val) {
        this.values[`${x}-${y}`] = val;
    }

    get(x, y) {
        const val = this.values[`${x}-${y}`];
        if (typeof val !== 'undefined') {
            return val;
        }
        return this.defaultVal;
    }
}



// WEBPACK FOOTER //
// ./src/packages/grid-packer/index.js